/**
 * VA-28098 — durable hold for recorded bytes that are not yet in an S3 part.
 *
 * THE PROBLEM THIS EXISTS FOR. S3 refuses any multipart part except the last
 * below 5 MiB, so the browser has no choice but to accumulate before it can
 * upload anything. Those accumulated bytes live in one place: the page. A
 * reload destroys them. Worse, if the threshold was never reached the upload
 * has no parts at all, and `_rotate_to_new_segment` correctly aborts an upload
 * it cannot complete — taking the entire session's video with it. That is how
 * interview-b07254a4 published 153s of a 5-minute interview.
 *
 * Lowering the part size shrinks that window but cannot close it. Flushing on
 * `pagehide` cannot close it either: the tail is megabytes, `fetch` with
 * `keepalive` is capped at 64 KiB, and an unloading page is not given time to
 * finish a PUT. Both are treatments for a page that is already dying.
 *
 * SO DO THE WORK EARLY INSTEAD. Every Blob is written here as it arrives, while
 * the page is healthy and an async write is perfectly reliable. Nothing has to
 * happen at unload at all. The next page reads what its predecessor left,
 * uploads it into the SAME upload, and only then asks the server to rotate — so
 * the rotation finds parts where there were none, completes a real segment, and
 * the manifest gets both halves.
 *
 * WHY A PART NUMBER TRAVELS WITH EACH CHUNK. Recovery has to reproduce the part
 * boundaries the dead page had already chosen, because a part it managed to
 * upload before dying must be replaced by the identical bytes, not merged into
 * a differently-shaped one. `PartBuffer` increments its counter at cut time, so
 * tagging each chunk with the counter's value AT ARRIVAL groups chunks into
 * exactly the parts they were destined for, with no extra bookkeeping.
 *
 * RE-UPLOADING IS SAFE. S3's UploadPart is last-write-wins per number, so a
 * recovered part either fills a gap or overwrites itself with the same bytes.
 * Chunks are dropped from the store only once their part is acknowledged, so
 * the store holds a superset of what is missing and never a subset.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** Groups tagged chunks into parts in ascending part order.
 *
 *  Shared by both stores so the grouping — the part that actually has to be
 *  right — is written and tested once. */
export const groupIntoParts = (chunks) => {
    const byPart = new Map();
    for (const c of chunks) {
        if (!c.blob || c.blob.size === 0)
            continue;
        const list = byPart.get(c.partNumber);
        if (list)
            list.push(c.blob);
        else
            byPart.set(c.partNumber, [c.blob]);
    }
    return [...byPart.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([partNumber, blobs]) => {
        const blob = new Blob(blobs);
        return { partNumber, blob, size: blob.size };
    });
};
/**
 * In-memory store. The fallback when IndexedDB is unavailable, and what the
 * tests drive.
 *
 * It cannot survive the page, so it does NOT fix the bug — it exists so that a
 * browser with IndexedDB blocked (private mode, storage disabled, quota denied)
 * degrades to exactly today's behaviour instead of throwing inside the
 * recording path.
 */
export class MemoryPendingChunkStore {
    constructor() {
        this.ref = null;
        this.chunks = [];
    }
    remember(ref) {
        return __awaiter(this, void 0, void 0, function* () {
            this.ref = ref;
        });
    }
    append(blob, partNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!blob || blob.size === 0)
                return;
            this.chunks.push({ blob, partNumber });
        });
    }
    dropPart(partNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            this.chunks = this.chunks.filter((c) => c.partNumber !== partNumber);
        });
    }
    recover() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.ref)
                return null;
            const parts = groupIntoParts(this.chunks);
            if (parts.length === 0)
                return null;
            return { ref: this.ref, parts };
        });
    }
    clear() {
        return __awaiter(this, void 0, void 0, function* () {
            this.ref = null;
            this.chunks = [];
        });
    }
}
const DB_NAME = 'vaarta-recording';
const DB_VERSION = 1;
const CHUNKS = 'chunks';
const REFS = 'refs';
const openDb = (slugUnused) => new Promise((resolve, reject) => {
    let req;
    try {
        req = indexedDB.open(DB_NAME, DB_VERSION);
    }
    catch (err) {
        reject(err);
        return;
    }
    req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(CHUNKS)) {
            // Auto-increment so arrival order is preserved without the caller
            // having to invent a key; `slug` indexes one interview's chunks.
            const store = db.createObjectStore(CHUNKS, { keyPath: 'id', autoIncrement: true });
            store.createIndex('slug', 'slug', { unique: false });
        }
        if (!db.objectStoreNames.contains(REFS)) {
            db.createObjectStore(REFS, { keyPath: 'slug' });
        }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('indexedDB open blocked'));
});
const promisify = (req) => new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
});
/**
 * IndexedDB-backed store, scoped to one interview slug.
 *
 * Deliberately thin: every method is one transaction and nothing else, because
 * this runs on the recording hot path and a stall here becomes backpressure on
 * the recorder. Errors are surfaced to the caller, which swallows them — a
 * failed durability write must never interrupt a recording that is otherwise
 * working.
 */
export class IndexedDbPendingChunkStore {
    constructor(slug) {
        this.slug = slug;
    }
    tx(stores, mode, body) {
        return __awaiter(this, void 0, void 0, function* () {
            const db = yield openDb();
            try {
                const tx = db.transaction(stores, mode);
                const out = yield body(tx);
                yield new Promise((resolve, reject) => {
                    tx.oncomplete = () => resolve();
                    tx.onerror = () => reject(tx.error);
                    tx.onabort = () => reject(tx.error);
                });
                return out;
            }
            finally {
                db.close();
            }
        });
    }
    remember(ref) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.tx([REFS], 'readwrite', (tx) => __awaiter(this, void 0, void 0, function* () {
                yield promisify(tx.objectStore(REFS).put(Object.assign({ slug: this.slug }, ref)));
            }));
        });
    }
    append(blob, partNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!blob || blob.size === 0)
                return;
            yield this.tx([CHUNKS], 'readwrite', (tx) => __awaiter(this, void 0, void 0, function* () {
                yield promisify(tx.objectStore(CHUNKS).add({ slug: this.slug, partNumber, blob }));
            }));
        });
    }
    dropPart(partNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.tx([CHUNKS], 'readwrite', (tx) => __awaiter(this, void 0, void 0, function* () {
                const store = tx.objectStore(CHUNKS);
                const rows = yield promisify(store.index('slug').getAll(this.slug));
                for (const row of rows) {
                    if (row.partNumber === partNumber)
                        store.delete(row.id);
                }
            }));
        });
    }
    recover() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const found = yield this.tx([REFS, CHUNKS], 'readonly', (tx) => __awaiter(this, void 0, void 0, function* () {
                const ref = yield promisify(tx.objectStore(REFS).get(this.slug));
                const rows = yield promisify(tx.objectStore(CHUNKS).index('slug').getAll(this.slug));
                return { ref, rows };
            }));
            if (!((_a = found.ref) === null || _a === void 0 ? void 0 : _a.uploadId) || !((_b = found.ref) === null || _b === void 0 ? void 0 : _b.key))
                return null;
            const parts = groupIntoParts((_c = found.rows) !== null && _c !== void 0 ? _c : []);
            if (parts.length === 0)
                return null;
            return { ref: { uploadId: found.ref.uploadId, key: found.ref.key }, parts };
        });
    }
    clear() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.tx([REFS, CHUNKS], 'readwrite', (tx) => __awaiter(this, void 0, void 0, function* () {
                const chunks = tx.objectStore(CHUNKS);
                const rows = yield promisify(chunks.index('slug').getAll(this.slug));
                for (const row of rows)
                    chunks.delete(row.id);
                tx.objectStore(REFS).delete(this.slug);
            }));
        });
    }
}
/** The real store where the browser supports it, an inert one where it does
 *  not. Never throws: a browser without durable storage must still record. */
export const createPendingChunkStore = (slug) => {
    try {
        if (typeof indexedDB !== 'undefined' && indexedDB !== null) {
            return new IndexedDbPendingChunkStore(slug);
        }
    }
    catch (_a) {
        /* fall through — some privacy modes throw on merely touching it */
    }
    return new MemoryPendingChunkStore();
};
